import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Clock, ExternalLink } from 'lucide-react';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';

interface StoreLocatorSectionProps {
  content: Record<string, unknown>;
}

interface LocalizedField { 'zh-HK'?: string; 'zh-CN'?: string; en?: string; }
interface Store {
  name: string | LocalizedField;
  address: string | LocalizedField;
  mapUrl: string;
  hours?: string | LocalizedField;
}

const StoreLocatorSection: React.FC<StoreLocatorSectionProps> = ({ content }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;
  const eyebrow = getLocalizedText(content.eyebrow, lang, t('locations.eyebrow'));
  const title = getLocalizedText(content.title, lang, t('locations.title'));
  const ctaText = getLocalizedText(content.ctaText, lang, t('locations.cta'));
  const stores = (content.stores as Store[] | undefined) || [];

  return (
    <section id="locations" className="relative py-12 md:py-16 bg-brand-stone overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-brand-cinnabar font-sans tracking-[0.25em] text-xs uppercase block mb-4">
            {eyebrow}
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-brand-ink mb-4">
            {title}
          </h2>
          <p className="text-brand-ink/60 font-sans max-w-2xl mx-auto">
            {ctaText}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {stores.map((store, index) => {
            const storeName = getLocalizedText(store.name, lang);
            const storeAddress = getLocalizedText(store.address, lang);
            const storeHours = getLocalizedText(store.hours, lang);
            return (
              <motion.div
                key={storeName + index}
                className="group bg-brand-paper p-8 border border-brand-ink/5 hover:border-brand-gold/30 transition-colors"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-brand-ink text-brand-gold rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-brand-ink mb-1">{storeName}</h3>
                    <p className="text-brand-ink/60 font-sans text-sm leading-relaxed">{storeAddress}</p>
                  </div>
                </div>

                {storeHours && (
                  <div className="flex items-center gap-2 text-brand-ink/50 text-sm font-sans mb-6 ml-16">
                    <Clock className="w-4 h-4" />
                    <span>{storeHours}</span>
                  </div>
                )}

                <div className="ml-16">
                  <motion.a
                    href={store.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-brand-ink text-brand-paper text-xs font-sans tracking-widest rounded-full hover:bg-brand-cinnabar transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {t('locations.navigate')}
                  </motion.a>
                </div>
              </motion.div>
            )})}
        </div>
      </div>
    </section>
  );
};

export default StoreLocatorSection;
